import { motion } from "framer-motion";
import { Beaker, Waves, Gauge, Wind, Atom, Sun, Fingerprint, Droplets, ShieldCheck, ArrowRight, FlaskConical } from "lucide-react";
import { Section, SectionHeading, Eyebrow } from "@/components/Section";

const METHODS = [
  { icon: Waves, abbr: "HPLC", name: "High-Performance Liquid Chromatography", d: "Separates a sample into its individual components so each compound can be measured on its own — a workhorse for verifying potency." },
  { icon: Gauge, abbr: "LC-MS/MS", name: "Liquid Chromatography–Tandem Mass Spectrometry", d: "Pairs chromatography with tandem mass spectrometry to identify and quantify compounds, including peptides, with high specificity." },
  { icon: Wind, abbr: "GC-MS", name: "Gas Chromatography–Mass Spectrometry", d: "Separates and identifies volatile compounds and residual solvents that liquid methods may not capture." },
  { icon: Atom, abbr: "ICP-MS", name: "Inductively Coupled Plasma Mass Spectrometry", d: "Detects trace heavy metals — such as lead, arsenic, cadmium and mercury — down to very low concentrations." },
  { icon: Sun, abbr: "UV-Vis", name: "UV-Visible Spectroscopy", d: "Measures how a sample absorbs ultraviolet and visible light to support quantitative assays of certain actives." },
  { icon: Fingerprint, abbr: "FTIR", name: "Fourier-Transform Infrared Spectroscopy", d: "Fingerprints molecular structure to help confirm the identity of raw materials and finished products." },
  { icon: Droplets, abbr: "Karl Fischer", name: "Karl Fischer Titration", d: "A precise technique for determining moisture (water) content, which can affect a product's stability and dosing." },
  { icon: ShieldCheck, abbr: "Micro", name: "Microbial Screening", d: "Screens for microbial contamination to help support overall product safety." },
];

const PANELS = ["Active potency", "Heavy metals", "Peptide identity & purity", "Allergen detection", "Fillers & adulterants", "Microbial screening", "Moisture content", "Dissolution behavior"];

const DIAGRAM = ["Supplement Sample", "Sample Preparation", "Separation (Chromatography)", "Detection (appropriate method)", "Data Analysis", "Clear Report"];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export default function TestingTechnology() {
  return (
    <div data-testid="testing-technology-page">
      <section className="bg-navy">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center md:px-10 md:py-24">
          <Eyebrow dark>The science</Eyebrow>
          <h1 className="mx-auto mt-5 max-w-3xl font-heading text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Our Testing <span className="font-serif font-normal italic text-lime">Technology</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-white/70">
            Our lab draws on a full suite of modern analytical instruments. We select the techniques that fit each supplement — this page explains, in general terms, how they work.
          </p>
        </div>
      </section>

      {/* Core principles */}
      <Section testid="tech-principles">
        <SectionHeading center eyebrow="Core principles"
          title={<>How we see <span className="font-serif font-normal italic">what's inside</span></>} />
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {[
            { icon: Waves, t: "Chromatography", d: "Chromatography separates the many compounds in a supplement using a specialized column, so each ingredient can be analyzed individually." },
            { icon: Gauge, t: "Mass Spectrometry", d: "Mass spectrometry identifies and quantifies the separated compounds with high precision — confirming both what is present and how much." },
          ].map((c) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="group rounded-3xl border border-border bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(47,76,120,0.12)]">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-periwinkle/40 transition-colors duration-300 group-hover:bg-navy"><c.icon className="h-6 w-6 text-navy transition-colors duration-300 group-hover:text-lime" /></span>
              <h3 className="mt-5 text-xl font-semibold text-navy">{c.t}</h3>
              <p className="mt-2 text-navy/60">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Methods grid */}
      <Section className="bg-white" testid="tech-methods">
        <SectionHeading center eyebrow="The instruments"
          title={<>A full <span className="font-serif font-normal italic">analytical toolkit</span></>}
          subtitle="A look at the techniques a professional lab can bring to supplement analysis. The right combination depends on the product." />
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {METHODS.map((m) => (
            <motion.div key={m.abbr} variants={item}
              className="group rounded-3xl border border-border bg-cream p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(47,76,120,0.14)]">
              <div className="flex items-center justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-periwinkle/40 transition-colors duration-300 group-hover:bg-navy">
                  <m.icon className="h-6 w-6 text-navy transition-colors duration-300 group-hover:text-lime" />
                </span>
                <span className="rounded-full bg-white px-3 py-1 font-heading text-xs font-bold text-navy ring-1 ring-border">{m.abbr}</span>
              </div>
              <h3 className="mt-5 text-base font-semibold leading-snug text-navy">{m.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-navy/60">{m.d}</p>
            </motion.div>
          ))}
        </motion.div>
        <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-navy/50">
          Not every method applies to every product. We select the techniques most appropriate for your supplement — this overview is informational and not a commitment to any specific procedure.
        </p>
      </Section>

      {/* Panels */}
      <Section testid="tech-panels">
        <SectionHeading center eyebrow="What we look for"
          title={<>Test types we can <span className="font-serif font-normal italic">perform</span></>} />
        <div className="mx-auto mt-12 flex max-w-4xl flex-wrap justify-center gap-3">
          {PANELS.map((p) => (
            <span key={p} className="rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-navy transition-all duration-200 hover:-translate-y-0.5 hover:border-navy hover:bg-navy hover:text-white">
              {p}
            </span>
          ))}
        </div>
      </Section>

      {/* Process diagram */}
      <Section className="bg-white" testid="tech-diagram">
        <SectionHeading center eyebrow="Process diagram"
          title={<>From sample to <span className="font-serif font-normal italic">clear report</span></>} />
        <div className="mt-12 flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-center md:gap-2">
          {DIAGRAM.map((step, i) => (
            <div key={step} className="flex flex-col items-center md:flex-row md:gap-2">
              <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`flex min-h-[76px] w-full items-center justify-center rounded-2xl border px-4 py-3 text-center text-sm font-semibold transition-transform duration-300 hover:scale-105 md:w-36 ${i === DIAGRAM.length - 1 ? "border-lime bg-lime text-navy" : i === 0 ? "border-navy bg-navy text-white" : "border-border bg-cream text-navy"}`}>
                {step}
              </motion.div>
              {i < DIAGRAM.length - 1 && <ArrowRight className="my-1 h-5 w-5 rotate-90 text-periwinkle-dark md:my-0 md:rotate-0" />}
            </div>
          ))}
        </div>
      </Section>

      {/* Detail */}
      <Section testid="tech-detail">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <img src="https://images.pexels.com/photos/7722652/pexels-photo-7722652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Scientist in lab" className="rounded-[2rem] border border-border object-cover shadow-[0_18px_50px_rgba(47,76,120,0.1)]" />
          <div>
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-periwinkle/40"><Beaker className="h-6 w-6 text-navy" /></span>
            <h2 className="mt-5 font-heading text-3xl font-semibold tracking-tight text-navy lg:text-4xl">Precision you can rely on</h2>
            <p className="mt-4 text-navy/65">These methods can detect ingredients at very low concentrations and distinguish between closely related compounds — essential for verifying peptides and actives, and for screening contaminants or fillers.</p>
            <p className="mt-4 text-navy/65">The result is a transparent, defensible measurement of what your supplement actually contains, presented in a clear, easy-to-read report.</p>
            <div className="mt-7 flex items-center gap-2 rounded-2xl border border-border bg-white p-4">
              <FlaskConical className="h-5 w-5 shrink-0 text-navy" />
              <p className="text-sm text-navy/70"><span className="font-semibold text-navy">7-day turnaround</span> on most reports.</p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
